/**
 * BEGAT – Cart Rewards Manager
 *
 * Levels:
 *   ≥ 2 boxes → free pick-up point delivery (automatic Shopify discount)
 *   ≥ 3 boxes → free gift of 3 protein bars (auto-added to cart)
 *   ≥ 4 boxes → free box of chosen flavour (picker shown in cart drawer)
 *
 * SETUP REQUIRED:
 *   1. GIFT_3BARS_HANDLE    – URL handle of your free 3-bar gift product
 *   2. FREE_BOX_HANDLES     – URL handle of each free-box product, one per flavour
 *      (admin → Products → your product → "Handle" field, or last part of the URL)
 *   3. MAIN_PRODUCT_HANDLES – URL handle of the real retail product sold under that
 *      flavour. The "-offert" gift products are separate Shopify products with
 *      their own inventory, so a flavour is only offered as a free box while
 *      BOTH its gift product AND its real retail product are in stock.
 */

(function () {
  'use strict';

  // ── CONFIGURE ─────────────────────────────────────────────────────────────
  const GIFT_3BARS_HANDLE = 'pack-echantillon-copie';
  const GIFT_3BARS_QTY    = 1;
  const FREE_BOX_HANDLES  = {           // one handle per flavour, leave '' to hide
    'Cacao':       'cacao-noisette-offert',
    'Cacahuète':   'beurre-de-cacahuete-offert',
    'Banane':      'banane-noisette-offert',
    'Mix': 'assortiment-3-saveurs-offert',
  };
  const MAIN_PRODUCT_HANDLES = { // real retail product whose stock gates each flavour
    'Cacao':       'barre-proteinee-au-cacao',
    'Cacahuète':   'barre-proteinee-au-beurre-de-cacahuete',
    'Banane':      'barre-proteinee-a-la-banane',
    'Mix': 'pack-variete',
  };
  // ──────────────────────────────────────────────────────────────────────────

  let syncing = false;
  /** @type {number|null} */
  let gift3BarsVariantId = null;
  /** @type {Record<string, number>} */
  const freeBoxVariantCache = {};
  /** @type {Record<string, boolean>} */
  const freeBoxAvailabilityCache = {};

  // ── variant resolvers ─────────────────────────────────────────────────────

  async function fetchProduct(/** @type {string} */ handle) {
    var r = await fetch('/products/' + handle + '.js');
    if (r.status === 429) {
      await new Promise(function (resolve) { setTimeout(resolve, 1000); });
      r = await fetch('/products/' + handle + '.js');
    }
    if (!r.ok) throw new Error('Produit introuvable (' + handle + ', HTTP ' + r.status + '). Vérifiez que le produit est publié.');
    return r.json();
  }

  async function resolveVariantFromHandle(/** @type {string} */ handle) {
    var p = await fetchProduct(handle);
    return /** @type {number} */ (p.variants[0].id);
  }

  async function resolveGift3BarsVariant() {
    if (gift3BarsVariantId) return gift3BarsVariantId;
    gift3BarsVariantId = await resolveVariantFromHandle(GIFT_3BARS_HANDLE);
    console.log('[bgt-rewards] gift variant resolved:', gift3BarsVariantId);
    return gift3BarsVariantId;
  }

  async function resolveFreeBoxVariant(/** @type {string} */ flavor) {
    if (freeBoxVariantCache[flavor]) return freeBoxVariantCache[flavor];
    var handle = FREE_BOX_HANDLES[/** @type {keyof typeof FREE_BOX_HANDLES} */ (flavor)];
    if (!handle) return 0;
    freeBoxVariantCache[flavor] = await resolveVariantFromHandle(handle);
    console.log('[bgt-rewards] free-box variant for', flavor, ':', freeBoxVariantCache[flavor]);
    return freeBoxVariantCache[flavor];
  }

  async function productIsAvailable(/** @type {string} */ handle) {
    if (handle in freeBoxAvailabilityCache) return freeBoxAvailabilityCache[handle];
    var available;
    try {
      var p = await fetchProduct(handle);
      available = Boolean(p.available) && p.variants.some(function (/** @type {{ available: boolean }} */ v) { return v.available; });
    } catch (e) {
      console.error('[bgt-rewards] availability check failed for', handle, ':', e);
      available = true; // fail open — don't block the offer over a network hiccup
    }
    freeBoxAvailabilityCache[handle] = available;
    return available;
  }

  // A flavour is only offerable while BOTH its gift ("-offert") product and its
  // real retail product are in stock — they're separate Shopify products with
  // independent inventory, so the gift product alone can look available even
  // after the actual flavour has sold out.
  async function resolveFreeBoxAvailability(/** @type {string} */ flavor) {
    var giftHandle = FREE_BOX_HANDLES[/** @type {keyof typeof FREE_BOX_HANDLES} */ (flavor)];
    if (!giftHandle) return false;
    var mainHandle = MAIN_PRODUCT_HANDLES[/** @type {keyof typeof MAIN_PRODUCT_HANDLES} */ (flavor)];
    var checks = [productIsAvailable(giftHandle)];
    if (mainHandle) checks.push(productIsAvailable(mainHandle));
    var results = await Promise.all(checks);
    return results.every(Boolean);
  }

  // ── cart helpers ──────────────────────────────────────────────────────────

  function countBoxes(cart) {
    return cart.items.reduce(function (n, item) {
      return item.properties && item.properties['_bgt_reward'] ? n : n + item.quantity;
    }, 0);
  }

  function findReward(cart, rewardType) {
    return cart.items.find(function (i) {
      return i.properties && i.properties['_bgt_reward'] === rewardType;
    }) || null;
  }

  function getSectionIds() {
    var ids = /** @type {string[]} */ ([]);
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      ids.push(/** @type {HTMLElement} */ (el).dataset.sectionId || '');
    });
    return ids.filter(Boolean);
  }

  function cartPost(url, body) {
    var ids = getSectionIds();
    if (ids.length) body.sections = ids.join(',');
    var payload = JSON.stringify(body);
    async function attempt(/** @type {number} */ retriesLeft) {
      var r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
      });
      if (r.status === 429 && retriesLeft > 0) {
        await new Promise(function (resolve) { setTimeout(resolve, 1000); });
        return attempt(retriesLeft - 1);
      }
      if (!r.ok) return r.json().then(function (e) { throw e; });
      return r.json();
    }
    return attempt(2);
  }

  function addRewardItem(variantId, qty, rewardType) {
    return cartPost('/cart/add.js', {
      id: variantId,
      quantity: qty,
      properties: { '_bgt_reward': rewardType },
    });
  }

  var internalRefresh = false;
  var internalRefreshTimer = 0;

  async function triggerCartRefresh(/** @type {Record<string,string>|undefined} */ sections) {
    internalRefresh = true;
    clearTimeout(internalRefreshTimer);
    internalRefreshTimer = setTimeout(function () { internalRefresh = false; }, 1000);
    // Fetch a fresh item_count so listeners (e.g. cart-icon.js) don't treat a
    // missing count as zero and stomp the bubble/sessionStorage cache.
    var itemCount;
    try {
      var freshCart = await fetch('/cart.js').then(function (r) { return r.json(); });
      itemCount = freshCart.item_count;
    } catch (e) {
      itemCount = undefined;
    }
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: { data: { sections: sections || {}, itemCount: itemCount } },
    }));
  }

  // ── UI helpers ────────────────────────────────────────────────────────────

  function showRewardsError(/** @type {string} */ msg) {
    var el = /** @type {HTMLElement|null} */ (document.querySelector('.bgt-rewards__msg'));
    if (!el) return;
    var prev = el.innerHTML;
    el.innerHTML = '<span style="color:#e55;">' + msg + '</span>';
    setTimeout(function () { el.innerHTML = prev; }, 4000);
  }

  async function renderFlavorPicker(/** @type {number} */ boxCount, /** @type {any} */ cart) {
    var picker = document.getElementById('bgt-flavor-picker');
    if (!picker) return;

    if (boxCount < 4 || findReward(cart, 'free_box')) {
      picker.innerHTML = '';
      return;
    }

    var flavors = Object.entries(FREE_BOX_HANDLES).filter(function (e) { return Boolean(e[1]); });
    if (!flavors.length) return;

    var availability = await Promise.all(flavors.map(function (e) { return resolveFreeBoxAvailability(e[0]); }));

    picker.innerHTML =
      '<p class="bgt-rewards__picker-title">Choisissez votre boite offerte :</p>' +
      '<div class="bgt-rewards__picker-btns">' +
      flavors.map(function (e, i) {
        var available = availability[i];
        return '<button class="bgt-rewards__flavor-btn" data-flavor="' + e[0] + '"' +
          (available ? '' : ' disabled title="Rupture de stock"') + '>' +
          e[0] + '</button>';
      }).join('') +
      '</div>';
  }

  // Re-derive picker visibility from a fresh cart snapshot. Needed after any
  // server-rendered morph (which wipes the client-injected picker markup) so the
  // offer still shows up even when it was triggered by an internal refresh.
  async function refreshPickerFromServer() {
    try {
      var cart = await fetch('/cart.js').then(function (r) { return r.json(); });
      await renderFlavorPicker(countBoxes(cart), cart);
      reorderCartItems();
    } catch (e) {
      console.error('[bgt-rewards] picker refresh error:', e);
    }
  }

  // Move reward rows to the top of the cart table (so they're visible without
  // scrolling) without duplicating them.
  function reorderCartItems() {
    var tbody = document.querySelector('.cart-items__table tbody');
    if (!tbody) return;
    var rows = Array.from(tbody.querySelectorAll('tr[data-key]'));
    var rewardRows = rows.filter(function (r) {
      return Boolean(/** @type {HTMLElement} */ (r).dataset.bgtReward);
    });
    if (!rewardRows.length) return;
    // Check if already ordered correctly (all reward rows before all non-reward rows)
    var lastReward = -1;
    var firstNonReward = rows.length;
    rows.forEach(function (r, i) {
      if (/** @type {HTMLElement} */ (r).dataset.bgtReward) {
        lastReward = i;
      } else if (i < firstNonReward) {
        firstNonReward = i;
      }
    });
    if (lastReward < firstNonReward) return;
    // Use a fragment so the reward rows keep their relative order when moved together.
    var frag = document.createDocumentFragment();
    rewardRows.forEach(function (r) { frag.appendChild(r); });
    tbody.insertBefore(frag, tbody.firstChild);
  }

  // ── main sync ─────────────────────────────────────────────────────────────

  async function syncRewards() {
    if (syncing) return;
    syncing = true;

    try {
      var cart = await fetch('/cart.js').then(function (r) { return r.json(); });
      var n = countBoxes(cart);
      var sections = /** @type {Record<string,string>} */ ({});
      var dirty = false;

      var giftItem    = findReward(cart, 'gift_3bars');
      var freeBoxItem = findReward(cart, 'free_box');

      // Add gift bars if threshold just reached
      if (n >= 3 && !giftItem) {
        try {
          var giftVariant = await resolveGift3BarsVariant();
          var addGift = await addRewardItem(giftVariant, GIFT_3BARS_QTY, 'gift_3bars');
          sections = addGift.sections || {};
          dirty = true;
        } catch (e) {
          var desc = (e && /** @type {any} */ (e).description) ? /** @type {any} */ (e).description : JSON.stringify(e);
          showRewardsError('Erreur cadeau : ' + desc);
        }
      }

      // Batch all removals / clamps into a single /cart/update.js call
      var /** @type {Record<string,number>} */ updates = {};
      if (giftItem && n < 3)                     updates[giftItem.key]    = 0;
      if (giftItem && giftItem.quantity > 1)      updates[giftItem.key]    = 1;
      if (freeBoxItem && n < 4)                   updates[freeBoxItem.key] = 0;

      if (Object.keys(updates).length > 0) {
        try {
          var batchResult = await cartPost('/cart/update.js', { updates: updates });
          sections = batchResult.sections || {};
          dirty = true;
        } catch (e) {
          console.error('[bgt-rewards] batch update failed:', e);
        }
      }

      if (dirty) {
        // The internal 'cart:update' this triggers is caught by our own listener
        // below, which re-checks the picker against a fresh cart snapshot once
        // the drawer morph (server-rendered, picker-less) has settled.
        triggerCartRefresh(sections);
      } else {
        await renderFlavorPicker(n, cart);
        reorderCartItems();
      }
    } catch (e) {
      console.error('[bgt-rewards] sync error:', e);
    } finally {
      syncing = false;
    }
  }

  // ── event wiring ──────────────────────────────────────────────────────────

  // Flavor button clicks — event delegation survives DOM morphing
  document.addEventListener('click', async function (e) {
    var btn = /** @type {HTMLButtonElement|null} */ (
      (/** @type {Element} */ (e.target)).closest('.bgt-rewards__flavor-btn')
    );
    if (!btn || btn.disabled) return;

    var flavor = btn.dataset.flavor || '';
    btn.disabled = true;
    btn.textContent = '…';

    try {
      if (!(await resolveFreeBoxAvailability(flavor))) {
        showRewardsError('Cette saveur est en rupture de stock.');
        btn.disabled = true;
        btn.title = 'Rupture de stock';
        btn.textContent = flavor;
        return;
      }
      var variantId = await resolveFreeBoxVariant(flavor);
      if (!variantId) {
        console.warn('[bgt-rewards] handle not configured for flavour:', flavor);
        btn.disabled = false;
        btn.textContent = flavor;
        return;
      }
      var addBox = await addRewardItem(variantId, 1, 'free_box');
      triggerCartRefresh(addBox.sections || {});
    } catch (err) {
      var msg = err instanceof Error ? err.message : JSON.stringify(err);
      console.error('[bgt-rewards] add free box failed:', msg);
      showRewardsError(msg);
      btn.disabled = false;
      btn.textContent = flavor;
    }
  });

  var syncTimer;
  var lastSyncTime = 0;
  var MIN_SYNC_INTERVAL = 2000;

  document.addEventListener('cart:update', function () {
    if (internalRefresh) {
      setTimeout(refreshPickerFromServer, 0);
      return;
    }
    clearTimeout(syncTimer);
    var wait = Math.max(500, MIN_SYNC_INTERVAL - (Date.now() - lastSyncTime));
    syncTimer = setTimeout(function () {
      lastSyncTime = Date.now();
      syncRewards();
    }, wait);
    setTimeout(reorderCartItems, 0);
  });

  setTimeout(syncRewards, 0);
})();
